import { showSettings, wireControls } from "./controls-panel.js";
import { asConvertOptions, optionFields, type ToolOptions } from "./options.js";
import { AccountSettings } from "./account-settings.js";
import type { ConvertOptions } from "@kensio/pinyinjs";
import { defaultOptions } from "./options.js";
import { readerLocale } from "./reader-writing.js";
import {
  browserStorage,
  saveOptions,
  storedOptions,
} from "./settings-store.js";

/**
 * The settings, from the last visit to the next and from one device to another.
 *
 * Loading them, drawing the panel, saving a change and telling the tool to
 * re-convert are one job rather than four: every one of them happens because
 * of one of the others, and none of them is interesting to the conversion
 * itself, which only ever wants the current `ConvertOptions`.
 *
 * **Four sources, in this order, and each speaks only for what it names.**
 *
 *  1. The link, which wins. Somebody was sent it and it says what it is for.
 *  2. This device, out of `localStorage`, which is what the last visit chose.
 *  3. The account, for a signed-in reader on a device holding nothing.
 *  4. The tools' defaults, with the site's writing seeded into the locale.
 *
 * **The device beats the account, and the other way round was considered.**
 * Treating the account as the authority and the device as a cache of it reads
 * better as a sentence and behaves worse: a reader who set the panel up while
 * signed out and then signed in would have that undone by an older copy, and
 * every signed-in visit would re-convert once when the answer arrived. What
 * the account is for is a device that has never been set up, and that is what
 * it does. A change writes both, so the two converge on whichever device the
 * reader last touched.
 */
export class ToolSettings {
  readonly #storage = browserStorage();
  readonly #account: AccountSettings;

  #options: ToolOptions;

  /**
   * Show the settings in the panel the page already has, and report every
   * change.
   *
   * The panel arrives with the defaults checked, so the first thing this does
   * is move the radios the last visit or the link disagrees with. The ids and
   * names that keep two panels on one page apart are the template's now,
   * which is why there is no longer a prefix to pass.
   *
   * The account is asked afterwards and only where it can answer, which is a
   * signed-in reader whose device stored nothing. `onChange` is called again
   * when it does, because by then the tool may have converted something.
   */
  constructor(
    panel: Element,
    fromUrl: Partial<ToolOptions>,
    onChange: () => void,
    page: Document = document,
  ) {
    this.#account = new AccountSettings(page);

    const stored = storedOptions(this.#storage);

    this.#options = { ...(stored ?? this.#seeded(page)), ...fromUrl };

    showSettings(panel, this.#options);

    wireControls<ToolOptions>(panel, optionFields, (changed) => {
      this.#options = { ...this.#options, ...changed };
      saveOptions(this.#storage, this.#options);
      void this.#account.save(this.#options);
      onChange();
    });

    if (stored === undefined) {
      void this.#fromAccount(panel, fromUrl, onChange);
    }
  }

  /**
   * The tools' defaults, with the writing the reader reads the site in.
   *
   * The one setting the panel and the site both have an opinion about. A
   * converter that opened on 简体 for somebody reading 繁體 everywhere else
   * would be the one page on the site that ignored them, and the panel's own
   * control is still there for a reader who wants this conversion done the
   * other way. See `readerLocale`.
   */
  #seeded(page: Document): ToolOptions {
    return { ...defaultOptions, locale: readerLocale(page) };
  }

  /**
   * Take what the account holds, where this device held nothing.
   *
   * The link still wins, since it is the most specific thing anybody said. What
   * arrives is written to this device as well, so the next visit needs no
   * request at all.
   */
  async #fromAccount(
    panel: Element,
    fromUrl: Partial<ToolOptions>,
    onChange: () => void,
  ): Promise<void> {
    const held = await this.#account.stored();

    if (held === undefined) {
      return;
    }

    this.#options = { ...held, ...fromUrl };

    saveOptions(this.#storage, this.#options);
    showSettings(panel, this.#options);
    onChange();
  }

  /** The settings as the conversion wants them. */
  get convertOptions(): ConvertOptions {
    return asConvertOptions(this.#options);
  }

  /** The settings in full, which is what the address bar is written from. */
  get options(): ToolOptions {
    return this.#options;
  }
}
